<template>
  <div class="sk-connect-page" v-if="$root.authCompleted">

    <div class="sk-topbar sk-topbar-with-action">
      <a href="#" class="sk-logo" @click.prevent>
        <img src="https://sentrykit.com/wp-content/uploads/2026/04/Sk-2.0-logo-white.png" alt="SentryKit" style="height:48px;width:auto;display:block;">
      </a>
      <button type="button" class="sk-topbar-logout" @click="$root.signOutUser()">Log out</button>
    </div>

    <div class="sk-steps-bar">
      <div class="sk-step sk-step-done">
        <span class="sk-step-num">✓</span>
        Create Account
      </div>
      <div class="sk-step-connector"></div>
      <div class="sk-step" :class="currentStep === 5 ? 'sk-step-done' : 'sk-step-active'">
        <span class="sk-step-num">{{ currentStep === 5 ? '✓' : '2' }}</span>
        Connect Amazon
      </div>
    </div>

    <div class="sk-main-wide">

      <div v-if="welcomeMode && currentStep === 4 && !loadingText" class="sk-welcome-strip">
        <template v-if="welcomeMode === 'sso'">
          <p class="sk-welcome-main">Your account is ready<span v-if="welcomeName">, {{ welcomeName }}</span>. One step left.</p>
          <p class="sk-welcome-sub">Signed in as {{ $root.userEmail }} · <button type="button" @click="notYou">Not you?</button></p>
        </template>
        <template v-else>
          <p class="sk-welcome-main">You're signed in as {{ $root.userEmail }}. One step left: connect your Amazon store.</p>
          <p class="sk-welcome-sub"><button type="button" @click="notYou">Not you?</button></p>
        </template>
      </div>

      <div v-if="loadingText" class="sk-two-col-card sk-loading-card">
        <div class="sk-spinner" style="margin: 0 auto 16px;"></div>
        <p style="color: var(--sk-text-mid, #4a6080);">{{ loadingText }}</p>
      </div>

      <div v-else-if="currentStep === 4 && freeSubscriptionReady" class="sk-two-col-card" key="step-4">

        <div class="sk-trust-panel">
          <h2 class="sk-steps-heading">What happens when you click Connect</h2>
          <ol class="sk-steps">
            <li>
              <span class="sk-step-n">1</span>
              <span>
                <strong>Approve on Amazon.</strong>
                Sign in to Seller Central and approve SentryKit for the marketplaces you picked.
              </span>
            </li>
            <li>
              <span class="sk-step-n">2</span>
              <span>
                <strong>Your listings load on their own.</strong>
                Every ASIN in those marketplaces is added for you. Nothing to upload or copy.
              </span>
            </li>
            <li>
              <span class="sk-step-n">3</span>
              <span>
                <strong>Monitoring starts.</strong>
                Alerts come to your email. Send them to Slack, Teams or ClickUp whenever you like.
              </span>
            </li>
          </ol>

          <div class="sk-help">
            <img class="sk-help-photo" :src="raghavPhoto" alt="Raghav from SentryKit">
            <div>
              <p class="sk-help-title">Rather connect it together?</p>
              <p class="sk-help-copy">I'm Raghav from SentryKit. Book a call and we'll connect your store together, or ask me anything first.</p>
              <div class="sk-help-actions">
                <button type="button" class="sk-help-primary" @click="bookCall">Book a call</button>
                <button type="button" class="sk-help-outline" @click="openChat">Chat with me</button>
              </div>
            </div>
          </div>
        </div>

        <div class="sk-form-panel sk-connect-form-panel">
          <div v-if="billingSetupWarning" class="sk-billing-warning">
            <span>{{ billingSetupWarning }}</span>
            <button type="button" class="sk-billing-warning-retry" @click="ensureFreeSubscriptionThenConnect">Retry</button>
          </div>
          <connect-region-form
            :working="connecting"
            :copying="copyingLink"
            :error="cardError"
            :show-teammate-link="true"
            :link-note="linkNote"
            @connect="startConnect"
            @copy-link="copyTeammateLink"
          />
        </div>
      </div>

      <div v-else-if="currentStep === 5" class="sk-two-col-card sk-success-card" key="step-5">
        <div class="sk-success-wrap">
          <div class="sk-success-icon">
            <svg fill="none" viewBox="0 0 24 24" stroke="#16a34a"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <h2>You're all set!</h2>
          <p>Your SentryKit account is ready. Amazon is loading your store and ASINs — this usually takes a few minutes.</p>
          <button class="sk-cta-btn" @click="goToDashboard" style="max-width:320px;margin:0 auto;">
            Go to Dashboard →
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import './styles/signup-flow.css'
import ConnectRegionForm from './components/ConnectRegionForm.vue'
import raghavPhoto from './assets/team/raghav-112.jpg'
import { pushGtmEvent, maybeFireConnectStoreSuccess } from './utils/gtmEvents'
import { startAmazonAuthorization } from './utils/amazonAuthorizationStart'
import { openCalendlyBooking } from './utils/calendly'

export default {
  components: { ConnectRegionForm },
  data: function () {
    return {
      currentStep: 4,
      selectedPlan: 'free',
      freeSubscriptionReady: false,
      loadingText: '',
      cardError: null,
      billingSetupWarning: null,
      connecting: false,
      copyingLink: false,
      linkNote: '',
      raghavPhoto: raghavPhoto,
      ssoWelcome: false,
      welcomeCaptured: false,
      planConfig: {
        free: { priceId: process.env.STRIPE_FREE_PLAN }
      },
      connectViewTracked: false
    }
  },
  computed: {
    welcomeMode: function () {
      if (this.ssoWelcome) return 'sso'
      if (this.$route.query.from === 'signup') return 'signup'
      return null
    },
    welcomeName: function () {
      return this.$root.userFirstName || ''
    }
  },
  methods: {
    ensureFreeSubscriptionThenConnect: function () {
      var self = this
      self.selectedPlan = 'free'
      self.cardError = null
      self.billingSetupWarning = null
      self.loadingText = 'Setting up your account...'
      var priceId = self.planConfig.free.priceId
      if (!priceId) {
        console.warn('[Connect] STRIPE_FREE_PLAN is not set; skipping subscription create (local dev).')
        self.loadingText = ''
        self.freeSubscriptionReady = true
        self.currentStep = 4
        self.$root.$emit('fb-pixel-event', 'standard', 'CompleteRegistration')
        return
      }
      self.$http.post('/api/billing/subscription/create', { priceId: priceId }).then(
        function () {
          self.loadingText = ''
          self.billingSetupWarning = null
          self.freeSubscriptionReady = true
          self.currentStep = 4
          self.$root.$emit('fb-pixel-event', 'standard', 'CompleteRegistration')
        },
        function () {
          self.loadingText = ''
          self.freeSubscriptionReady = true
          self.currentStep = 4
          self.$root.$emit('fb-pixel-event', 'standard', 'CompleteRegistration')
        }
      )
    },
    handleSetupReturn: function () {
      var urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('setup_complete') === 'true') {
        window.history.replaceState({}, document.title, '/connect')
        this.ensureFreeSubscriptionThenConnect()
      }
    },
    goToDashboard: function () {
      this.$router.push('/welcome')
      this.$root.$emit('fb-pixel-event', 'custom', 'LoggedInToApp')
    },
    captureWelcome: function () {
      if (this.welcomeCaptured) return
      this.welcomeCaptured = true
      if (this.$root.connectWelcome) {
        this.ssoWelcome = true
        this.$root.connectWelcome = null
      }
    },
    notYou: function () {
      this.$root.signOutUser('/signup')
    },
    startConnect: function (params) {
      var self = this
      if (self.connecting) return
      self.connecting = true
      self.cardError = null
      var query = Object.assign({ path: '/connect' }, params)
      startAmazonAuthorization(self.$http, {
        params: query,
        method: self.ssoWelcome ? 'google' : 'email',
        gtm: { region: params.region, marketplaces: params.marketplaceCodes }
      }).then(function () {}, function (message) {
        self.connecting = false
        self.cardError = message || 'Failed to start authorization'
      })
    },
    copyTeammateLink: function () {
      var self = this
      if (self.copyingLink) return
      self.copyingLink = true
      self.linkNote = ''
      self.$http.post('/api/v1/connect-invites').then(
        function (response) {
          var url = response.body && response.body.url
          self.copyingLink = false
          if (!url) {
            self.cardError = 'Could not create a link. Try again.'
            return
          }
          var done = function () { self.linkNote = 'Link copied.' }
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, function () { self.linkNote = url })
          } else {
            self.linkNote = url
          }
        },
        function () {
          self.copyingLink = false
          self.cardError = 'Could not create a link. Try again.'
        }
      )
    },
    bookCall: function () {
      openCalendlyBooking({
        name: this.$root.userFullName || this.$root.userFirstName || '',
        email: this.$root.userEmail || ''
      }, 'connect')
    },
    openChat: function () {
      pushGtmEvent({ event: 'connect_chat_click', source: 'connect' })
      if (typeof window.Intercom === 'function') window.Intercom('showNewMessage')
    },
    initState: function () {
      if (!this.$root.authCompleted) return
      this.captureWelcome()
      if (this.$root.paymentStatus !== 1 && this.$root.anySellerAccountSetupCompleted) {
        var isRecentConnection = this.$root.secondsSinceFirstSellerAccountCreated != null &&
          this.$root.secondsSinceFirstSellerAccountCreated < 9999
        var target = isRecentConnection ? '/welcome' : (this.$root.defaultPath || '/home')
        maybeFireConnectStoreSuccess()
        this.$router.push(target)
        return
      }

      this.cardError = null
      if (this.$root.paymentStatus === 2 || this.$root.paymentStatus === 0) {
        this.loadingText = ''
        this.freeSubscriptionReady = true
        this.currentStep = 4
        this.$root.$emit('fb-pixel-event', 'standard', 'CompleteRegistration')
      } else {
        this.freeSubscriptionReady = false
        this.ensureFreeSubscriptionThenConnect()
      }
    }
  },
  watch: {
    '$root.authCompleted': function (val) {
      if (!val) return
      this.initState()
    },
    currentStep: function () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    freeSubscriptionReady: function (val) {
      if (val && this.currentStep === 4 && !this.connectViewTracked) {
        this.connectViewTracked = true
        pushGtmEvent({ event: 'connect_store_view', step: 2, step_name: 'connect_amazon' })
      }
    }
  },
  created: function () {
    this.initState()
  },
  mounted: function () {
    var el = document.getElementById('preloadr')
    if (el) {
      el.style.transition = 'opacity 0.5s'
      el.style.opacity = '0'
      setTimeout(function () { el.style.display = 'none' }, 500)
    }
    this.handleSetupReturn()
    var self = this
    this.onPageShow = function (e) {
      if (e.persisted) self.connecting = false
    }
    window.addEventListener('pageshow', this.onPageShow)
  },
  beforeDestroy: function () {
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow)
  }
}
</script>

<style scoped>
.sk-connect-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Roboto', 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  background: var(--sk-bg, #f0f4f9);
  overflow-x: hidden;
}
.sk-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  min-height: 320px;
}
.sk-success-card {
  display: block;
  padding: 52px 48px;
  max-width: 640px;
}
.sk-connect-form-panel {
  overflow-y: auto;
}
.sk-welcome-strip {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto 14px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.sk-welcome-main {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.sk-welcome-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}
.sk-welcome-sub button {
  background: none;
  border: 0;
  padding: 0;
  color: #f5841f;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}
.sk-steps-heading {
  margin: 0 0 16px;
  font-size: 18px;
  line-height: 1.3;
  color: #fff;
}
.sk-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.sk-steps li {
  display: flex;
  gap: 10px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.88);
}
.sk-steps strong {
  color: #fff;
}
.sk-step-n {
  flex: 0 0 22px;
  height: 22px;
  border-radius: 999px;
  background: rgba(245, 132, 31, 0.9);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sk-help {
  display: flex;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.sk-help-photo {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.sk-help-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}
.sk-help-copy {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);
}
.sk-help-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.sk-help-primary,
.sk-help-outline {
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}
.sk-help-primary {
  background: #fff;
  color: #0f172a;
  border: 1px solid #fff;
}
.sk-help-outline {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.7);
}
.sk-billing-warning {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.45;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
}
.sk-billing-warning-retry {
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #b45309;
  background: #fff;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  cursor: pointer;
}
.sk-topbar-with-action { position: relative; }
.sk-topbar-logout {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,.12);
  border: 1px solid rgba(255,255,255,.22);
  color: rgba(255,255,255,.85);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 16px;
  border-radius: 8px;
  cursor: pointer;
}
.sk-topbar-logout:hover {
  background: rgba(255,255,255,.22);
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// ConnectSellerAccountPage.vue?25317782